import { useMemo, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import FlightMultiCartReviewTile from '../FlightMultiCartReviewTile'
import BedbankHotelTile from './BedbankHotelTile'
import LeHotelTile from './LeHotelTile'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import Divider from 'LuxKit/Divider'
import clsx from 'clsx'
import { isFlightItem, isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import MultiCartReviewTileOverlay from '../_MultiCartReviewTileOverlay'
import UndoDeletedItemsTile from '../../UndoDeletedItemsTile'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { MultiCartReviewMachineContext } from '../../MultiCartReviewMachineContext'
import Sheet from 'components/utils/Sheet'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import IconButton from 'LuxKit/Button/IconButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useOffer from 'hooks/Offers/useOffer'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { useSelector } from '@xstate/react'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEAdmin } from 'selectors/accountSelectors'
import SharedCartComments from '../../Comments/SharedCartComments'
import { isIncompleteReturnFlightMultiCartItem } from '../../multiCartUtils'

const FlightHotelBundleMultiCartReviewGroupContainer = styled(Pane)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: none;
  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
  &.is-condensed {
    border: none;
  }
`

const DodgyButton = styled(IconButton)`
  border: none;
`

const FlightHotelBundleMultiCartReviewGroupContent = styled(Group)`
  position: relative;
`

const StyledFlightMultiCartReviewTile = styled(FlightMultiCartReviewTile)`
  border: none;
`

interface Props {
  multiCartItems: Array<App.MultiCartFlightItem | App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem>
  tileVariant?: 'default' | 'condensed'
  banner?: React.ReactNode
  showOverlay?: boolean
}

function FlightHotelBundleMultiCartReviewGroup({
  multiCartItems,
  tileVariant = 'default',
  banner,
  showOverlay = false,
}: Props) {
  const intl = useIntl()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type === 'shared'

  const { flightItems, accommodationItems } = useMemo(() => {
    const flightItems: Array<App.MultiCartFlightItem> = []
    const accommodationItems: Array<App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem> = []

    multiCartItems.forEach((multiCartItem) => {
      if (isFlightItem(multiCartItem.item)) {
        flightItems.push(multiCartItem as App.MultiCartFlightItem)
      } else {
        accommodationItems.push(multiCartItem as App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem)
      }
    })

    return { flightItems, accommodationItems }
  }, [multiCartItems])

  const displayableFlightItems = useMemo(
    () => flightItems.filter((flightItem): boolean => !isIncompleteReturnFlightMultiCartItem(flightItem)),
    [flightItems],
  )

  const hotelItem = accommodationItems[0]?.item as App.Checkout.BedbankHotelItem | App.Checkout.InstantBookingLEHotelItem | undefined
  const [offer] = useOffer<App.HotelOffer | App.BedbankOffer>(hotelItem?.offerId)

  // Check if all items in the bundle are deleted
  const areAllItemsDeleted = useMemo(
    () => multiCartItems.every(item => item.state === 'deleted'),
    [multiCartItems],
  )

  // Check if all items are saved for later
  const areAllItemsSavedForLater = useMemo(
    () => multiCartItems.every(item => item.state === 'save_for_later'),
    [multiCartItems],
  )

  // Check if any item is deleted (for disabling buttons)
  const isAnyItemDeleted = useMemo(
    () => multiCartItems.some(item => item.state === 'deleted'),
    [multiCartItems],
  )

  // Get bundle title from the hotel item
  const flightHotelBundleLabel = intl.formatMessage({
    id: 'Cxe-ttAQ9MO2IqXfEt9E',
    defaultMessage: 'Flight + Hotel Bundle',
    description: 'Default title for a flight and hotel bundle group in the cart review page',
  })
  const bundleTitle = useMemo(() => {
    const hotelItem = accommodationItems[0]
    if (!hotelItem) return flightHotelBundleLabel

    const hotelName = isBedbankItem(hotelItem.item)
      ? hotelItem.item.offerId
      : (hotelItem.item as App.Checkout.InstantBookingLEHotelItem).offerId

    return intl.formatMessage(
      {
        id: 'yxN1CLQyktbEGnsoHam3',
        defaultMessage: '{hotelName} + Flight Bundle',
        description: 'Title for a named hotel and flight bundle group in the cart review page',
      },
      { hotelName },
    )
  }, [accommodationItems, flightHotelBundleLabel, intl])

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => multiCartItems.map(item => item.id), [multiCartItems])

  // Handlers for bundle-level actions
  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: allItemIds,
      saveForLater: !areAllItemsSavedForLater,
      skipNotification: true,
    })
  }, [activeCartId, allItemIds, areAllItemsSavedForLater, multiCartReviewActorRef])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId!,
      itemIds: allItemIds,
      currentStates: allItemIds.reduce((acc, id) => ({ ...acc, [id]: multiCartItems.find(item => item.id === id)?.state }), {}),
    })
  }, [activeCartId, allItemIds, multiCartActorRef, multiCartItems])

  const handleUndoDelete = useCallback(() => {
    multiCartActorRef.send({
      itemIds: allItemIds,
      type: 'undo delete',
    })
  }, [allItemIds, multiCartActorRef])

  const isCondensed = tileVariant === 'condensed'

  const getItemsForAccommodationRow = useCallback((accommodationItem: App.MultiCartBedbankHotelItem | App.MultiCartInstantBookingLEHotelItem) => {
    const associatedFlightItems = displayableFlightItems.filter(flightItem =>
      flightItem.bundledItemIds?.includes(accommodationItem.item.itemId) ??
      !flightItem.bundledItemIds?.length,
    )

    return [
      accommodationItem,
      ...associatedFlightItems,
    ]
  }, [displayableFlightItems])

  // If all items are deleted, show undo tile instead of the tiles
  if (areAllItemsDeleted) {
    return (
      <FlightHotelBundleMultiCartReviewGroupContainer className={clsx({ 'is-condensed': isCondensed })}>
        {banner}
        <UndoDeletedItemsTile
          title={bundleTitle}
          onUndoClick={handleUndoDelete}
        />
      </FlightHotelBundleMultiCartReviewGroupContainer>
    )
  }

  const isMultiRoom = accommodationItems.length > 1
  const hotelName = offer?.property.name || offer?.name || intl.formatMessage(productLabels.hotel)
  const dateRange = hotelItem ? formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut) : undefined
  const summary = (() => {
    if (!dateRange || !hotelItem) {
      return undefined
    }
    const roomCount = accommodationItems.length
    const duration = hotelItem.duration

    const roomLabel = intl.formatMessage(
      {
        id: 'tfptkBh2JSEbu_nJa-9u',
        defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
        description: 'Room count for flight+hotel bundle cart summary in the shopping cart',
      },
      { roomCount },
    )
    const nightLabel = intl.formatMessage(
      {
        id: 'Ef51wv6cirU1sh2YISDa',
        defaultMessage: '{duration, plural, one {# night} other {# nights}}',
        description: 'Night count for flight+hotel bundle cart summary in the shopping cart',
      },
      { duration },
    )
    return intl.formatMessage(
      {
        id: '_0llGI5B6IGy_Hn3iCen',
        defaultMessage: '{dateRange} · {roomLabel} · {nightLabel}',
        description: 'Date range, room count, and duration summary for a grouped flight and hotel bundle cart tile',
      },
      { dateRange, roomLabel, nightLabel },
    )
  })()

  const disableButtons = areAllItemsDeleted || isAnyItemDeleted

  const buttons = (
    <>
      {!areAllItemsSavedForLater && !isCondensed && (
        <TextButton
          kind="secondary"
          size="small"
          shape="round"
          disabled={disableButtons}
          onClick={handleSaveForLater}
        >
          {intl.formatMessage({
            id: 't9MCz0XofAuiUYduNguE',
            defaultMessage: 'Save all for later',
            description: 'Button to save all items in a flight+hotel bundle for later',
          })}
        </TextButton>
      )}
      {areAllItemsSavedForLater && !isCondensed && (
        <TextButton
          kind="primary"
          size="small"
          shape="round"
          disabled={disableButtons}
          onClick={handleSaveForLater}
        >
          {intl.formatMessage({
            id: 'bmS2bcveQTEA87zoSVkJ',
            defaultMessage: 'Add all',
            description: 'Button to add all saved items back to the flight+hotel bundle',
          })}
        </TextButton>
      )}
      {!isCondensed && (
        <TextButton
          kind="secondary"
          size="small"
          shape="round"
          disabled={disableButtons}
          startIcon={<LineTrashAltIcon />}
          onClick={handleDelete}
        >
          {intl.formatMessage({
            id: 'IDuBOJ44mFBEglL-Lto2',
            defaultMessage: 'Delete all',
            description: 'Button to delete all items in a flight+hotel bundle',
          })}
        </TextButton>
      )}
      {isCondensed && (
        <DodgyButton
          kind="secondary"
          horizontalOutdent="end"
          verticalOutdent="start"
          onClick={handleDelete}
        >
          <LineTrashAltIcon />
        </DodgyButton>
      )}
    </>
  )

  return (
    <FlightHotelBundleMultiCartReviewGroupContainer className={clsx({ 'is-condensed': isCondensed })}>
      {banner}
      <FlightHotelBundleMultiCartReviewGroupContent direction="vertical">
        {isMultiRoom && (
          <Sheet
            background="normal-background"
            border="transparent"
            borderRadius="S"
            padding={isCondensed ? 16 : 20}
          >
            <Group direction="horizontal" horizontalAlign="space-between">
              <Group direction="vertical" gap={2}>
                <Heading variant="heading6">{hotelName}</Heading>
                {summary && <BodyText variant="medium">{summary}</BodyText>}
              </Group>

              <Group direction="horizontal" gap={8}>
                {buttons}
              </Group>
            </Group>
          </Sheet>
        )}
        {accommodationItems.map((accommodationItem) => (
          <React.Fragment key={accommodationItem.id}>
            <Divider kind="primary" direction="horizontal" />
            {isBedbankItem(accommodationItem.item) && (
              <BedbankHotelTile
                multiCartItems={getItemsForAccommodationRow(accommodationItem)}
                tileVariant={tileVariant}
                hideTitleAndDates={isMultiRoom}
                hideButtons={isMultiRoom}
              />
            )}
            {isInstantBookingLEHotelItem(accommodationItem.item) && (
              <LeHotelTile
                multiCartItems={getItemsForAccommodationRow(accommodationItem)}
                tileVariant={tileVariant}
                hideTitleAndDates={isMultiRoom}
                hideButtons={isMultiRoom}
              />
            )}
          </React.Fragment>
        ))}
        {displayableFlightItems.map((flightItem) => (
          <React.Fragment key={flightItem.id}>
            <Divider kind="primary" direction="horizontal" />
            <StyledFlightMultiCartReviewTile
              multiCartItem={flightItem}
              tileVariant={tileVariant}
              hideActionButtons
              hidePrices
              hidePassengerCount
              hideLoyaltyPoints
            />
          </React.Fragment>
        ))}
        {isMultiRoom && (
          <CSSBreakpoint max="mobile">
            <Divider kind="primary" direction="horizontal" />
            <Sheet
              background="normal-background"
              border="transparent"
              borderRadius="S"
              padding="16 16"
            >
              <Group direction="horizontal" gap={8}>
                {buttons}
              </Group>
            </Sheet>
          </CSSBreakpoint>
        )}
        {activeCartId && !isCondensed && isSharedCart && (
          <SharedCartComments
            multiCartId={activeCartId}
            itemIds={allItemIds}
            isAdmin={isAdmin}
            noBorder
          />
        )}

        {showOverlay && <MultiCartReviewTileOverlay />}
      </FlightHotelBundleMultiCartReviewGroupContent>
    </FlightHotelBundleMultiCartReviewGroupContainer>
  )
}

export default FlightHotelBundleMultiCartReviewGroup
